import { can } from '../utils/permissions';
import authService from '../services/authService';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from './Sidebar';
import roomService from '../services/roomService ';
import admissionService from '../services/admissionService';

/*
  ARCHITECTURE:
  - roomService.getTower(tower)  → returns TowerInfo with floors[].frontRooms/rearRooms[].beds[]
  - roomService.getStats()       → totalBeds, vacantBeds, occupiedBeds, towerA/B breakdowns
  - admissionService.getEligibleStudents() → students for modal search dropdown
  - roomService.allocateBed(admissionId, bedCode, studentName, studentRegId)
  - roomService.vacateBed(bedCode)

  BedCode format from backend: "AA-111-B1", "AF-231-B2" etc.
  Bed status from backend: "VACANT" or "OCCUPIED"
*/

const TOTAL_FLOORS = 7;

const TOWERS = {
  A: { label: 'Tower A', landmark: 'Convenience Store' },
  B: { label: 'Tower B', landmark: 'Dining Hall' },
};

/* ─────────────────────────────────────────────────────────
   ALLOCATION MODAL
──────────────────────────────────────────────────────────── */
const VACATE_REASONS = [
  'End of Term',
  'Shifting to Another Bed',
  'Termination',
  'Other (Personal)',
];

const AllocationModal = ({ bed, onClose, onAllocate, onVacate, students, isStudent }) => {
  const [query,       setQuery]       = useState('');
  const [filtered,    setFiltered]    = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [showDrop,    setShowDrop]    = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [vacating,    setVacating]    = useState(false);
  const [vacateReason, setVacateReason] = useState('');
  const inputRef   = useRef(null);
  const wrapperRef = useRef(null);

  const isOccupied = bed.status === 'OCCUPIED';
  const showDuesSummary = vacateReason !== '' && vacateReason !== 'Shifting to Another Bed';

  useEffect(() => {
    if (!isOccupied) inputRef.current?.focus();
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOccupied]);

  const search = (val) => {
    setQuery(val); setSelected(null);
    if (val.length < 2) { setFiltered([]); setShowDrop(false); return; }
    const q = val.toLowerCase();
    const res = students.filter(s =>
      (s.fullName || '').toLowerCase().includes(q) ||
      (s.admissionNumber || '').toString().includes(q) ||
      (s.registrationId || '').toString().includes(q)
    ).slice(0, 10);
    setFiltered(res); setShowDrop(true);
  };

  const pick = (s) => { setSelected(s); setQuery(s.fullName); setShowDrop(false); };

  const handleAllocate = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await onAllocate(
        selected.id || selected.studentId,
        bed.bedCode,
        selected.fullName,
        selected.admissionNumber || selected.registrationId,
      );
      onClose();
    } catch (e) { alert(e.message || 'Failed to allocate bed'); }
    finally { setSaving(false); }
  };

  const handleVacate = async () => {
    if (!vacateReason) { alert('Please select a reason for vacating.'); return; }
    setVacating(true);
    try {
      await onVacate(bed.bedCode);
      onClose();
    } catch (e) { alert(e.message || 'Failed to vacate bed'); }
    finally { setVacating(false); }
  };

  const inp = {
    width: '100%', padding: '12px 15px', border: '2px solid #E8E4DF', borderRadius: 6,
    fontSize: 14, fontFamily: 'Work Sans, sans-serif', outline: 'none',
    color: '#2C2C2C', boxSizing: 'border-box', background: 'white',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'white', padding: 40, borderRadius: 12, maxWidth: 500, width: '90%' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ marginBottom: 30 }}>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, color: '#1B3B5F', marginBottom: 6 }}>
            {isOccupied && isStudent ? 'Bed Info' : isOccupied ? 'Vacate Bed' : isStudent ? 'Bed Info' : 'Allocate Bed'}
          </div>
          <div style={{ fontSize: 13, color: '#6B6B6B', fontWeight: 600, letterSpacing: 0.5 }}>
            {bed.bedCode}
          </div>
        </div>

        {isOccupied && isStudent ? (
          /* ── STUDENT VIEW: occupied bed — show bed code only, no private info ── */
          <>
            <div style={{ background:'#FFF0F0', border:'1px solid #FFCDD2', borderRadius:8, padding:'20px', marginBottom:24, textAlign:'center' }}>
              <div style={{ fontSize:36, marginBottom:12 }}>🛏️</div>
              <div style={{ fontWeight:700, fontSize:18, color:'#1B3B5F', marginBottom:6 }}>{bed.bedCode}</div>
              <div style={{ fontSize:13, color:'#DC3545', fontWeight:600 }}>Occupied</div>
              <div style={{ fontSize:12, color:'#9B9B9B', fontStyle:'italic', marginTop:8 }}>
                This bed is currently occupied.
              </div>
            </div>
            <button onClick={onClose} style={{ width:'100%', padding:15, border:'none', borderRadius:6, fontWeight:700, fontSize:13, textTransform:'uppercase', letterSpacing:1, cursor:'pointer', background:'#1B3B5F', color:'white' }}>
              Close
            </button>
          </>
        ) : isOccupied ? (
          /* ── ADMIN VIEW: VACATE BED MODAL ── */
          <>
            {/* Student Name (readonly) */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display:'block', fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', marginBottom:8 }}>Student Name</label>
              <input readOnly style={{ ...inp, background:'white' }} value={bed.studentName || '—'} />
            </div>

            {/* Stream (readonly) */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display:'block', fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', marginBottom:8 }}>Stream</label>
              <input readOnly style={{ ...inp, background:'white' }} value={bed.stream || bed.course || '—'} />
            </div>

            {/* Reason for Vacating */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display:'block', fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', marginBottom:8 }}>Reason for Vacating</label>
              <select
                value={vacateReason}
                onChange={e => setVacateReason(e.target.value)}
                style={{ ...inp, appearance:'auto', cursor:'pointer' }}
              >
                <option value="">Select reason...</option>
                {VACATE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            {/* Pending Dues Warning — shown for all reasons except Shifting to Another Bed */}
            {showDuesSummary && (() => {
              const total    = Number(bed.totalFees   || 0);
              const paid     = Number(bed.paidFees    || 0);
              const deposit  = Number(bed.refundableDeposit || 0);
              const balance  = total - paid;
              const hasDues  = balance > 0;
              return (
                <div style={{ background:'#FFFBEB', border:'1px solid #F59E0B', borderRadius:8, padding:'16px 18px', marginBottom:20 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                    <span style={{ fontSize:18 }}>⚠️</span>
                    <span style={{ fontWeight:700, fontSize:14, color:'#2C2C2C' }}>Pending Dues Summary</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #FDE68A' }}>
                    <span style={{ fontSize:13, color:'#6B6B6B' }}>Total Fees</span>
                    <span style={{ fontSize:13, fontWeight:600, color:'#2C2C2C' }}>₹{total.toLocaleString('en-IN')}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #FDE68A' }}>
                    <span style={{ fontSize:13, color:'#6B6B6B' }}>Amount Paid</span>
                    <span style={{ fontSize:13, fontWeight:600, color:'#2D5F4F' }}>₹{paid.toLocaleString('en-IN')}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #FDE68A' }}>
                    <span style={{ fontSize:13, fontWeight:700, color: hasDues ? '#DC3545' : '#2D5F4F' }}>Balance Due</span>
                    <span style={{ fontSize:13, fontWeight:700, color: hasDues ? '#DC3545' : '#2D5F4F' }}>₹{balance.toLocaleString('en-IN')}</span>
                  </div>
                  {deposit > 0 && (
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #FDE68A' }}>
                      <span style={{ fontSize:13, color:'#6B6B6B' }}>Refundable Deposit</span>
                      <span style={{ fontSize:13, fontWeight:600, color:'#1B3B5F' }}>₹{deposit.toLocaleString('en-IN')} (to be returned)</span>
                    </div>
                  )}
                  <p style={{ margin:'10px 0 0', fontSize:12, color:'#92400E', fontStyle:'italic' }}>
                    {hasDues
                      ? '⚠️ Outstanding balance exists. Please collect dues before vacating.'
                      : '✅ No outstanding dues. Safe to vacate.'}
                  </p>
                </div>
              );
            })()}

            {/* Action buttons */}
            <div style={{ display:'flex', gap:15, marginTop:10 }}>
              <button onClick={onClose} style={{ flex:1, padding:15, border:'none', borderRadius:6, fontWeight:700, fontSize:13, textTransform:'uppercase', letterSpacing:1, cursor:'pointer', background:'#E8E4DF', color:'#2C2C2C' }}>
                Cancel
              </button>
              <button
                onClick={handleVacate}
                disabled={vacating || !vacateReason}
                style={{ flex:1, padding:15, border:'none', borderRadius:6, fontWeight:700, fontSize:13, textTransform:'uppercase', letterSpacing:1, cursor:(vacating || !vacateReason) ? 'not-allowed' : 'pointer', background:(vacating || !vacateReason) ? '#aaa' : '#DC3545', color:'white', transition:'background 0.2s' }}
              >
                {vacating ? 'Vacating…' : 'Vacate Bed'}
              </button>
            </div>
          </>
        ) : isStudent ? (
          /* ── STUDENT VIEW: vacant bed — show info only ── */
          <>
            <div style={{ background:'#F0F7FF', border:'1px solid #BFDBFE', borderRadius:8, padding:'20px', marginBottom:24, textAlign:'center' }}>
              <div style={{ fontSize:36, marginBottom:12 }}>🛏️</div>
              <div style={{ fontWeight:700, fontSize:18, color:'#1B3B5F', marginBottom:6 }}>{bed.bedCode}</div>
              <div style={{ fontSize:13, color:'#6B6B6B', marginBottom:4 }}>This bed is currently <strong style={{ color:'#2D5F4F' }}>Available</strong></div>
              <div style={{ fontSize:12, color:'#9B9B9B', fontStyle:'italic', marginTop:8 }}>
                Bed allocation is confirmed by the hostel admin during admission.
              </div>
            </div>
            <div style={{ background:'#FFFBEB', border:'1px solid #F59E0B', borderRadius:8, padding:'14px 16px', marginBottom:24, fontSize:13, color:'#92400E' }}>
              💡 To request this bed, please mention bed code <strong>{bed.bedCode}</strong> to the hostel admin at the time of admission confirmation.
            </div>
            <button onClick={onClose} style={{ width:'100%', padding:15, border:'none', borderRadius:6, fontWeight:700, fontSize:13, textTransform:'uppercase', letterSpacing:1, cursor:'pointer', background:'#1B3B5F', color:'white' }}>
              Close
            </button>
          </>
        ) : (
          /* ── ADMIN VIEW: search student + allocate ── */
          <>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B', marginBottom: 8 }}>Search Student</label>
              <div ref={wrapperRef} style={{ position: 'relative' }}>
                <input ref={inputRef} style={inp} placeholder="Type name or registration ID..."
                  value={query} onChange={e => search(e.target.value)}
                  onFocus={() => filtered.length && setShowDrop(true)} />
                {showDrop && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '2px solid #1B3B5F', borderTop: 'none', borderRadius: '0 0 6px 6px', maxHeight: 250, overflowY: 'auto', zIndex: 10 }}>
                    {filtered.length === 0
                      ? <div style={{ padding: '12px 15px', color: '#6B6B6B', fontSize: 13 }}>No students found</div>
                      : filtered.map(s => (
                          <div key={s.id || s.studentId} onClick={() => pick(s)}
                            style={{ padding: '12px 15px', borderBottom: '1px solid #E8E4DF', cursor: 'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#F8F6F3'}
                            onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                            <div style={{ fontWeight: 700, color: '#1B3B5F', marginBottom: 3 }}>{s.fullName}</div>
                            <div style={{ fontSize: 12, color: '#6B6B6B', display: 'flex', gap: 15 }}>
                              <span>ID: {s.admissionNumber || s.registrationId}</span>
                              <span>{s.course || s.stream || '—'}</span>
                            </div>
                          </div>
                        ))
                    }
                  </div>
                )}
              </div>
            </div>
            {[
              { label: 'Student ID',   val: selected?.admissionNumber || selected?.registrationId || '' },
              { label: 'Student Name', val: selected?.fullName || '' },
              { label: 'Stream',       val: selected?.course || selected?.stream || '' },
              { label: 'Bed Code',     val: bed.bedCode },
            ].map(f => (
              <div key={f.label} style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B', marginBottom: 8 }}>{f.label}</label>
                <input readOnly style={{ ...inp, background: '#F8F6F3', color: f.val ? '#2C2C2C' : '#aaa' }} value={f.val} placeholder="—" />
              </div>
            ))}

            {/* ── Room Type Match Indicator ── */}
            {selected && (() => {
              const typeLabel = {
                '4sharing':'4 Sharing','3sharing':'3 Sharing','2sharing':'2 Sharing (Twin)',
                '2sharingPremium':'2 Sharing (Premium)','private':'Single / Private'
              };
              const studentType = selected.roomType;
              const bedRoomType = bed.roomType;
              if (!studentType || !bedRoomType) return null;
              const matches = studentType === bedRoomType;
              return (
                <div style={{
                  marginBottom: 18, padding: '12px 16px', borderRadius: 8,
                  background: matches ? '#F0FFF4' : '#FFF5F5',
                  border: `2px solid ${matches ? '#4CAF50' : '#E85D4A'}`,
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <span style={{ fontSize: 22 }}>{matches ? '✅' : '❌'}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: matches ? '#2D5F4F' : '#E85D4A' }}>
                      {matches ? 'Room Type Match' : 'Room Type Mismatch'}
                    </div>
                    <div style={{ fontSize: 12, color: '#6B6B6B', marginTop: 2 }}>
                      Student selected: <strong>{typeLabel[studentType] || studentType}</strong>
                      {' · '}
                      This bed: <strong>{typeLabel[bedRoomType] || bedRoomType}</strong>
                    </div>
                    {!matches && (
                      <div style={{ fontSize: 11, color: '#E85D4A', marginTop: 4, fontStyle: 'italic' }}>
                        ⚠️ Allocating a different room type than what was selected at admission.
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            <div style={{ display: 'flex', gap: 15, marginTop: 30 }}>
              <button onClick={onClose} style={{ flex: 1, padding: 15, border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', background: '#E8E4DF', color: '#2C2C2C' }}>Cancel</button>
              {(() => {
                const studentType = selected?.roomType;
                const bedType     = bed.roomType;
                const mismatch    = studentType && bedType && studentType !== bedType;
                const isDisabled  = !selected || saving || mismatch;
                return (
                  <button
                    onClick={!isDisabled ? handleAllocate : undefined}
                    disabled={isDisabled}
                    title={mismatch ? 'Cannot allocate — room type does not match student selection' : ''}
                    style={{ flex: 1, padding: 15, border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13,
                      textTransform: 'uppercase', letterSpacing: 1,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      background: mismatch ? '#E0E0E0' : selected && !saving ? '#1B3B5F' : '#aaa',
                      color: mismatch ? '#9B9B9B' : 'white',
                      opacity: mismatch ? 0.7 : 1,
                    }}>
                    {saving ? 'Allocating…' : mismatch ? '⛔ Type Mismatch' : 'Allocate'}
                  </button>
                );
              })()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   LABEL HELPERS
   roomCode "BR-21" → Tower B | Rear | Floor 2 | Room 1
   bedCode  "BR-211"→ BR-211  (shown as-is inside the room card)

   Room code format: {T}{W}-{F}{R}
     T = tower letter  (A or B)
     W = wing letter   (R=Rear, F=Front)
     F = floor digit   (1–7)
     R = room number   (1–5)
──────────────────────────────────────────────────────────── */
const parseRoomCode = (roomCode) => {
  // e.g. "BR-21"  → prefix="BR", floor="2", roomNum="1"
  //      "AF-53"  → prefix="AF", floor="5", roomNum="3"
  const prefix = roomCode.substring(0, 2);           // "BR"
  const rest   = roomCode.substring(3);               // "21"
  const tower  = prefix[0];                           // "B"
  const wing   = prefix[1];                           // "R"
  const floor  = rest.substring(0, rest.length - 1); // "2"
  const room   = rest.substring(rest.length - 1);    // "1"
  const towerLabel = tower === 'A' ? 'Tower A' : 'Tower B';
  const wingLabel  = wing  === 'R' ? 'Rear'    : 'Front';
  return { towerLabel, wingLabel, floor, room };
};

const RoomLabel = ({ roomCode }) => {
  const { towerLabel, wingLabel, floor, room } = parseRoomCode(roomCode);
  return (
    <div style={{ textAlign: 'center', marginBottom: 8 }}>
      {/* Room code - big and bold */}
      <div style={{ fontSize: 12, fontWeight: 800, color: '#1B3B5F', letterSpacing: 0.5 }}>
        {roomCode}
      </div>
      {/* Human-readable breakdown */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 2, marginTop: 3 }}>
        {[towerLabel, wingLabel, `Floor ${floor}`, `Room ${room}`].map((part, i, arr) => (
          <React.Fragment key={i}>
            <span style={{ fontSize: 8, fontWeight: 600, color: '#9B9B9B', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {part}
            </span>
            {i < arr.length - 1 && (
              <span style={{ fontSize: 8, color: '#C8C8C8', margin: '0 1px' }}>·</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   BED BOX — uses real bed data from backend
──────────────────────────────────────────────────────────── */
/* ── Bed SVG illustration ── */
const BedSVG = ({ occupied }) => (
  <svg viewBox="0 0 80 52" xmlns="http://www.w3.org/2000/svg" style={{ width:'100%', height:'auto', display:'block' }}>
    {/* Bed frame */}
    <rect x="2" y="20" width="76" height="28" rx="4" fill={occupied ? '#b91c1c' : '#9ca3af'} />
    {/* Headboard */}
    <rect x="2" y="8" width="13" height="40" rx="3" fill={occupied ? '#991b1b' : '#6b7280'} />
    {/* Mattress */}
    <rect x="17" y="16" width="61" height="26" rx="3" fill={occupied ? '#fee2e2' : '#f3f4f6'} />
    {/* Pillow */}
    <rect x="20" y="18" width="20" height="13" rx="3" fill={occupied ? '#fecaca' : '#e5e7eb'} stroke={occupied ? '#f87171' : '#d1d5db'} strokeWidth="1.2" />
    {/* Blanket */}
    <rect x="42" y="22" width="34" height="16" rx="2" fill={occupied ? '#ef4444' : '#d1d5db'} opacity="0.55" />
    {/* Blanket fold line */}
    <line x1="42" y1="22" x2="76" y2="22" stroke={occupied ? '#dc2626' : '#9ca3af'} strokeWidth="1.2" />
    {/* Legs */}
    <rect x="4"  y="46" width="5" height="5" rx="1" fill={occupied ? '#7f1d1d' : '#4b5563'} />
    <rect x="71" y="46" width="5" height="5" rx="1" fill={occupied ? '#7f1d1d' : '#4b5563'} />
  </svg>
);

const BedBox = ({ bed, onClick, isStudent }) => {
  const [hover, setHover] = useState(false);
  const occupied = bed.status === 'OCCUPIED';

  return (
    <div onClick={() => onClick(bed)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        borderRadius: 8,
        border: `2px solid ${occupied ? '#DC3545' : hover ? '#4CAF50' : '#D0D0D0'}`,
        background: occupied ? '#FFF5F5' : hover ? '#F0FFF4' : '#FAFAFA',
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '6px 4px 4px',
        gap: 2,
        transform: hover ? 'translateY(-2px)' : 'none',
        boxShadow: hover ? '0 4px 12px rgba(0,0,0,0.12)' : 'none',
        transition: 'all 0.2s ease',
        position: 'relative',
        boxSizing: 'border-box',
      }}>
      {/* Window badge */}
      {bed.windowBed && (
        <span style={{ position:'absolute', top:3, right:3, fontSize:7, fontWeight:800,
          color: occupied ? '#DC3545' : '#1565C0',
          background: occupied ? 'rgba(220,53,69,0.12)' : '#E3F0FF',
          padding:'1px 4px', borderRadius:3 }}>W</span>
      )}
      {/* Bed SVG */}
      <div style={{ width:'100%', padding:'0 3px' }}>
        <BedSVG occupied={occupied} />
      </div>
      {/* Bed code */}
      <div style={{ fontSize: 9, fontWeight: 800,
        color: occupied ? '#DC3545' : '#374151', letterSpacing: 0.3 }}>
        {bed.bedCode}
      </div>
      {/* Student name if occupied */}
      {occupied && bed.studentName && (
        <div style={{ fontSize: 7, fontWeight: 600, color: '#991b1b',
          textAlign:'center', maxWidth:'100%',
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {bed.studentName.split(' ')[0]}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   ROOM CARD — renders a single room with its beds from backend
──────────────────────────────────────────────────────────── */
const RoomCard = ({ room, onBedClick, isStudent }) => {
  const occupied = room.occupiedBeds || 0;
  const total    = room.capacity || room.beds?.length || 0;
  const { towerLabel, wingLabel, floor, room: roomNum } = parseRoomCode(room.roomCode);
  // ✅ Use room_type from backend — handles AF-73 change from 3sharing to 2sharingPremium
  const isPremium = room.roomType === '2sharingPremium';

  return (
    <div style={{ background: 'white', border: `3px solid ${isPremium ? '#D4AF37' : '#1B3B5F'}`, borderRadius: 8, padding: 12, flex: 1, minWidth: 0, boxShadow: isPremium ? '0 2px 8px rgba(212,175,55,0.2)' : '0 2px 8px rgba(27,59,95,0.1)' }}>

      {/* ── Room Header ── */}
      <div style={{ marginBottom: 10 }}>
        {/* Row 1: room code + occupancy badge + premium tag */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#1B3B5F', letterSpacing: 0.5 }}>
              {room.roomCode}
            </span>
            {isPremium && (
              <span style={{ fontSize:8, fontWeight:700, padding:'2px 6px', borderRadius:8, background:'#FBF3D5', color:'#8B6914', border:'1px solid #D4AF37', whiteSpace:'nowrap', letterSpacing:0.5 }}>
                ⭐ PREMIUM
              </span>
            )}
          </div>
          <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8, whiteSpace: 'nowrap',
            background: occupied === total ? '#FFF0F0' : occupied > 0 ? '#FFF8E8' : '#F0FFF4',
            color:      occupied === total ? '#DC3545'  : occupied > 0 ? '#F59E0B' : '#4CAF50',
            border:    `1px solid ${occupied === total ? '#DC3545' : occupied > 0 ? '#F59E0B' : '#4CAF50'}` }}>
            {occupied}/{total}
          </span>
        </div>
        {/* Row 2: Tower · Wing · Floor N · Room N */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          {[towerLabel, wingLabel, `Floor ${floor}`, `Room ${roomNum}`].map((part, i, arr) => (
            <React.Fragment key={i}>
              <span style={{ fontSize: 8, fontWeight: 600, color: '#9B9B9B', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {part}
              </span>
              {i < arr.length - 1 && (
                <span style={{ fontSize: 8, color: '#D0D0D0', margin: '0 1px' }}>·</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── Bed Grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(total, 4)}, 1fr)`, gap: 6 }}>
        {(room.beds || []).map(bed => (
          <BedBox key={bed.bedCode} bed={bed} onClick={onBedClick} isStudent={isStudent} />
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   FLOOR VIEW — renders a floor using REAL backend data
   floorData: { floor, frontRooms: [], rearRooms: [], totalBeds, vacantBeds, occupiedBeds }
──────────────────────────────────────────────────────────── */
const FloorView = ({ tower, floor, floorData, onBedClick, isStudent }) => {
  const isGround  = floor === 1;
  const frontRooms = floorData?.frontRooms || [];
  const rearRooms  = floorData?.rearRooms  || [];
  const total    = floorData?.totalBeds    || 0;
  const occupied = floorData?.occupiedBeds || 0;

  // Split rooms into visual rows matching the screenshot layout:
  // REAR Row1: first room (usually 4-bed wide)
  // REAR Row2: remaining rooms
  // FRONT Row1: first 3 rooms (2+3+2 bed groups)
  // FRONT Row2: remaining rooms
  const rearRow1  = rearRooms.slice(0, 1);
  const rearRow2  = rearRooms.slice(1);
  const frontRow1 = frontRooms.slice(0, 3);
  const frontRow2 = frontRooms.slice(3);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', background: 'white', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', position: 'relative', padding: '30px 30px 30px 110px', minHeight: 500 }}>

      {/* Wing Sidebar */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 80, display: 'flex', flexDirection: 'column', background: 'linear-gradient(180deg, #1B3B5F 0%, #0D1F2D 100%)', borderRadius: '12px 0 0 12px', overflow: 'hidden' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '2px solid rgba(255,255,255,0.3)' }}>
          <span style={{ writingMode: 'vertical-rl', color: 'white', fontSize: 13, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase' }}>Front</span>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ writingMode: 'vertical-rl', color: 'white', fontSize: 13, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase' }}>Rear</span>
        </div>
      </div>

      {/* FRONT SECTION */}
      <div style={{ marginBottom: 8 }}>
        {isGround ? (
          <div style={{ background: 'white', border: '2px solid #E8E4DF', padding: '50px 40px', textAlign: 'center', borderRadius: 8, fontWeight: 600, color: '#6B6B6B', fontSize: 18, marginBottom: 8 }}>
            {TOWERS[tower].landmark}
          </div>
        ) : frontRooms.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#aaa', fontSize: 13 }}>No front rooms on this floor</div>
        ) : (
          <>
            {frontRow1.length > 0 && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                {frontRow1.map(room => <RoomCard key={room.roomCode} room={room} onBedClick={onBedClick} isStudent={isStudent} />)}
              </div>
            )}
            {frontRow2.length > 0 && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                {frontRow2.map(room => <RoomCard key={room.roomCode} room={room} onBedClick={onBedClick} isStudent={isStudent} />)}
              </div>
            )}
          </>
        )}
      </div>

      {/* Divider */}
      <div style={{ borderTop: '3px solid #E8E4DF', margin: '16px 0' }} />

      {/* REAR SECTION */}
      <div>
        {rearRow1.length > 0 && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
            {rearRow1.map(room => <RoomCard key={room.roomCode} room={room} onBedClick={onBedClick} isStudent={isStudent} />)}
          </div>
        )}
        {rearRow2.length > 0 && (
          <div style={{ display: 'flex', gap: 12 }}>
            {rearRow2.map(room => <RoomCard key={room.roomCode} room={room} onBedClick={onBedClick} isStudent={isStudent} />)}
          </div>
        )}
      </div>

      {/* Floor Badge */}
      <div style={{ position: 'fixed', right: 40, top: '50%', transform: 'translateY(-50%)', background: 'white', width: 100, height: 100, borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 50 }}>
        <div style={{ fontSize: 30, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif', color: '#1B3B5F', lineHeight: 1 }}>{floor}</div>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B' }}>Floor</div>
        <div style={{ fontSize: 10, marginTop: 4, color: '#4CAF50', fontWeight: 600 }}>{occupied}/{total}</div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   MAIN SCREEN
──────────────────────────────────────────────────────────── */
const RoomAllocationScreen = ({ onNavigate, onLogout }) => {
  const user = authService.getCurrentUser();
  const role = user?.role?.toUpperCase() || '';
  const isStudent = role === 'STUDENT';
  const [showRates,   setShowRates]   = useState(false);
  const [rates,       setRates]       = useState({ rate2:0, rate2p:0, rate4:0, ratep:0, effectiveFrom:'', history:[] });
  const [rateForm,    setRateForm]    = useState({ rate4:'', rate3:'', rate2:'', rate2p:'', ratep:'', effectiveFrom:new Date().toISOString().slice(0,10), notes:'' });
  const [rateSaving,  setRateSaving]  = useState(false);
  const [rateToast,   setRateToast]   = useState('');
  const [bedToast,    setBedToast]    = useState('');
  const [tower,      setTower]      = useState('A');
  const [floor,      setFloor]      = useState(1);
  const [towerData,  setTowerData]  = useState({ A: null, B: null });
  const [students,   setStudents]   = useState([]);
  const [stats,      setStats]      = useState({ totalBeds: 0, occupiedBeds: 0, vacantBeds: 0, towerATotal: 0, towerAOccupied: 0, towerAVacant: 0, towerBTotal: 0, towerBOccupied: 0, towerBVacant: 0 });
  const [activeBed,  setActiveBed]  = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [towerA, towerB, roomStats, eligibleStudents, rateData] = await Promise.all([
        roomService.getTower('A'),
        roomService.getTower('B'),
        roomService.getStats(),
        admissionService.getEligibleStudents().catch(() => []),
        roomService.getRates().catch(() => ({ rate2:0, rate2p:0, rate4:0, ratep:0, effectiveFrom:'', history:[] })),
      ]);
      setTowerData({ A: towerA, B: towerB });
      setStats(roomStats);
      setStudents(eligibleStudents);
      setRates(rateData);
      setRateForm(f => ({ ...f, rate4: rateData.rate4||'', rate3: rateData.rate3||'', rate2: rateData.rate2||'', rate2p: rateData.rate2p||'', ratep: rateData.ratep||'' }));
    } catch (e) {
      setError(e.message || 'Failed to load room data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Get floor data for current tower+floor
  const currentTowerData = towerData[tower];
  const currentFloorData = currentTowerData?.floors?.find(f => f.floor === floor) || null;

  const handleAllocate = async (admissionId, bedCode, studentName, studentRegId) => {
    try {
      await roomService.allocateBed(admissionId, bedCode, studentName, studentRegId);
    } catch (e) {
      alert(e.message || 'Failed to allocate bed');
      return;
    }
    await load();
    setBedToast(`✅ Bed ${bedCode} allocated successfully!`);
    setTimeout(() => setBedToast(''), 6000);
  };

  const handleVacate = async (bedCode) => {
    await roomService.vacateBed(bedCode);
    await load();
    setBedToast(`🛏 Bed ${bedCode} vacated successfully!`);
    setTimeout(() => setBedToast(''), 6000);
  };

  const occupancyPct = stats.totalBeds > 0 ? Math.round((stats.occupiedBeds / stats.totalBeds) * 100) : 0;

  const navBtn = (dir, label, disabled) => (
    <button onClick={() => setFloor(f => Math.min(TOTAL_FLOORS, Math.max(1, f + dir)))} disabled={disabled}
      style={{ width: 50, height: 50, borderRadius: '50%', background: disabled ? 'rgba(255,255,255,0.3)' : '#1B3B5F', color: 'white', border: 'none', fontSize: 22, cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', opacity: disabled ? 0.3 : 1 }}
      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = '#D4AF37'; e.currentTarget.style.color = '#1B3B5F'; e.currentTarget.style.transform = 'scale(1.1)'; }}}
      onMouseLeave={e => { e.currentTarget.style.background = disabled ? 'rgba(255,255,255,0.3)' : '#1B3B5F'; e.currentTarget.style.color = 'white'; e.currentTarget.style.transform = 'scale(1)'; }}>
      {label}
    </button>
  );

  const saveRates = async () => {
    if (!rateForm.effectiveFrom) { alert('Please select an effective date.'); return; }
    setRateSaving(true);
    try {
      const data = await roomService.saveRates({
        rate4: parseFloat(rateForm.rate4)||0,
        rate3: parseFloat(rateForm.rate3)||0,
        rate2: parseFloat(rateForm.rate2)||0,
        rate2p: parseFloat(rateForm.rate2p)||0,
        ratep: parseFloat(rateForm.ratep)||0,
        effectiveFrom: rateForm.effectiveFrom,
        notes: rateForm.notes||null,
      });
      setRates(data);
      setRateForm(f => ({ ...f, rate4: data.rate4||'', rate3: data.rate3||'', rate2: data.rate2||'', rate2p: data.rate2p||'', ratep: data.ratep||'' }));
      setShowRates(false);
      setRateToast('✅ Room rates saved successfully!');
      setTimeout(() => setRateToast(''), 6000);
    } catch(e) { alert('Failed to save: ' + (e.response?.data?.message || e.message)); }
    finally { setRateSaving(false); }
  };

  const fmtINR = (n) => '₹' + (Number(n)||0).toLocaleString('en-IN');

  return (
    <div className="app-layout">
      <Sidebar activeItem="rooms" onNavigate={onNavigate} onLogout={onLogout} />

      <main className="main-content" style={{ padding: 0, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

        {/* TOP BAR */}
        <div style={{ background: 'white', padding: '20px 40px', borderBottom: '1px solid #E8E4DF', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 32, fontWeight: 400, color: '#1B3B5F', margin: 0 }}>Room Allocation</h1>
          <div style={{ display: 'flex', gap: 10, alignItems:'center' }}>
            {!isStudent && can('MODIFY_RATE') && (
              <button onClick={() => setShowRates(true)}
                style={{ padding:'10px 18px', background:'transparent', border:'2px solid #D4AF37', color:'#D4AF37', borderRadius:6, cursor:'pointer', fontSize:13, fontWeight:700, display:'flex', alignItems:'center', gap:8, fontFamily:'Work Sans,sans-serif' }}
                onMouseEnter={e=>{ e.currentTarget.style.background='#D4AF37'; e.currentTarget.style.color='white'; }}
                onMouseLeave={e=>{ e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#D4AF37'; }}>
                ⚙️ Room Rate Settings
              </button>
            )}
            {['A', 'B'].map(key => {
              const isActive = tower === key;
              return (
                <button key={key} onClick={() => { setTower(key); setFloor(1); }}
                  style={{ padding: '12px 30px', border: '2px solid #1B3B5F', background: isActive ? '#1B3B5F' : 'white', color: isActive ? 'white' : '#1B3B5F', fontWeight: 700, fontSize: 14, cursor: 'pointer', borderRadius: 6, transition: 'all 0.3s', textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'Work Sans, sans-serif' }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#E8E4DF'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'white'; }}>
                  🏢 Tower {key}
                </button>
              );
            })}
          </div>
        </div>

        {/* STATS BAR */}
        <div style={{ padding: '30px 40px', background: 'white', borderBottom: '1px solid #E8E4DF' }}>
          <div style={{ maxWidth: 1400, margin: '0 auto' }}>
            {/* 4 navy stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 30 }}>
              {[
                { label: 'Total Beds', value: stats.totalBeds },
                { label: 'Occupied',   value: loading ? '…' : stats.occupiedBeds },
                { label: 'Vacant',     value: loading ? '…' : stats.vacantBeds },
                { label: 'Occupancy',  value: loading ? '…' : `${occupancyPct}%` },
              ].map((s, i) => (
                <div key={i} style={{ background: 'linear-gradient(135deg, #1B3B5F 0%, #0D1F2D 100%)', padding: 25, borderRadius: 12, textAlign: 'center', color: 'white' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, opacity: 0.85, marginBottom: 10 }}>{s.label}</div>
                  <div style={{ fontSize: 42, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif', lineHeight: 1 }}>{s.value}</div>
                </div>
              ))}
            </div>
            {/* Tower breakdown */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, padding: 20, background: '#F8F6F3', borderRadius: 12 }}>
              {[
                { key: 'A', total: stats.towerATotal, occupied: stats.towerAOccupied, vacant: stats.towerAVacant },
                { key: 'B', total: stats.towerBTotal, occupied: stats.towerBOccupied, vacant: stats.towerBVacant },
              ].map(({ key, total, occupied, vacant }) => {
                const isActive = tower === key;
                return (
                  <div key={key} onClick={() => { setTower(key); setFloor(1); }}
                    style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', padding: 15, background: 'white', borderRadius: 8, cursor: 'pointer', border: `2px solid ${isActive ? '#D4AF37' : '#E8E4DF'}`, boxShadow: isActive ? '0 4px 15px rgba(212,175,55,0.2)' : 'none', transition: 'all 0.3s' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1B3B5F', textTransform: 'uppercase', letterSpacing: 1.5 }}>🏢 Tower {key}</div>
                    {[{ label: 'Beds', val: total }, { label: 'Occupied', val: loading ? '…' : occupied }, { label: 'Vacant', val: loading ? '…' : vacant }].map((s, i) => (
                      <div key={i} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B', marginBottom: 5 }}>{s.label}</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#1B3B5F', fontFamily: 'Cormorant Garamond, serif' }}>{s.val}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {error && (
          <div style={{ margin: '16px 40px', padding: '12px 16px', background: '#FFF3F3', borderLeft: '4px solid #E85D4A', color: '#E85D4A', fontSize: 13, borderRadius: 2 }}>
            {error}
            <button onClick={load} style={{ marginLeft: 15, padding: '4px 12px', background: '#E85D4A', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Retry</button>
          </div>
        )}

        {/* FLOOR LAYOUT */}
        <div style={{ position: 'relative', padding: 40, overflow: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ padding: 80, textAlign: 'center', color: '#6B6B6B', fontSize: 15 }}>Loading rooms…</div>
          ) : (
            <FloorView
              key={`${tower}-${floor}`}
              tower={tower}
              floor={floor}
              floorData={currentFloorData}
              onBedClick={setActiveBed} isStudent={isStudent}
            />
          )}
        </div>

        {/* FLOOR NAV */}
        <div style={{ position: 'fixed', right: 160, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: 15, zIndex: 51 }}>
          {navBtn(-1, '▲', floor === 1)}
          {navBtn(+1, '▼', floor === TOTAL_FLOORS)}
        </div>

        {/* LEGEND */}
        <div style={{ position: 'fixed', bottom: 30, left: 320, background: 'white', padding: 20, borderRadius: 8, boxShadow: '0 4px 15px rgba(0,0,0,0.1)', display: 'flex', gap: 20, zIndex: 40 }}>
          {[{ color: '#D3D3D3', label: 'Vacant' }, { color: '#DC3545', label: 'Occupied' }, { color: '#4CAF50', label: 'Available' }].map(l => (
            <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 600 }}>
              <div style={{ width: 28, height: 28, borderRadius: 4, background: l.color, border: '2px solid #ddd' }} />
              <span>{l.label}</span>
            </div>
          ))}
        </div>

        {/* FOOTER */}
        <div style={{ background: '#333', color: 'white', textAlign: 'center', padding: 14, fontSize: 12 }}>
          2026 Copyright © Omkara Hostel. All Rights Reserved.
        </div>

      </main>

      {/* ALLOCATION / VACATE MODAL */}
      {activeBed && isStudent !== undefined && (
        <AllocationModal
          bed={activeBed}
          students={students}
          onClose={() => setActiveBed(null)}
          onAllocate={handleAllocate}
          onVacate={handleVacate}
          isStudent={isStudent}
        />
      )}

      {/* ── Rate Settings Modal ── */}
      {showRates && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1300, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
          onClick={e => e.target === e.currentTarget && setShowRates(false)}>
          <div style={{ background:'#fff', borderRadius:16, width:640, maxWidth:'95vw', maxHeight:'92vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
            {/* Header */}
            <div style={{ background:'linear-gradient(135deg,#1B3B5F 0%,#0D1F2D 100%)', padding:'28px 32px', borderRadius:'16px 16px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <h2 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:26, fontWeight:400, color:'#fff', margin:0 }}>⚙️ Room Rate Settings</h2>
                <p style={{ fontSize:12, color:'rgba(255,255,255,0.65)', letterSpacing:1, textTransform:'uppercase', marginTop:4 }}>Configure monthly accommodation charges by room type</p>
              </div>
              <button onClick={() => setShowRates(false)} style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'white', width:34, height:34, borderRadius:'50%', fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
            </div>
            {/* Body */}
            <div style={{ padding:32 }}>
              <div style={{ background:'#EFF6FF', border:'1.5px solid #BFDBFE', borderRadius:10, padding:'14px 18px', marginBottom:28, display:'flex', gap:12 }}>
                <span style={{ fontSize:18 }}>ℹ️</span>
                <p style={{ fontSize:13, color:'#1e40af', lineHeight:1.5, margin:0 }}>These rates are used as master data during the admission process. When a student is assigned a room, fees are auto-calculated using these rates. Changes take effect from the date specified below.</p>
              </div>
              <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:2, color:'#6B6B6B', marginBottom:20, paddingBottom:8, borderBottom:'2px solid #E8E4DF' }}>Monthly Rates by Room Type</div>
              {[
                { sharing:4,    icon:'🏢', title:'4 Sharing Room',           desc:'Quad occupancy — 4 beds per room',    key:'rate4' },
                { sharing:'2t', icon:'🛏️', title:'2 Sharing Room (Twin)',     desc:'Double occupancy — standard twin',    key:'rate2' },
                { sharing:'3s', icon:'🔵', title:'3 Sharing Room',              desc:'Triple occupancy',                    key:'rate3'  },
                { sharing:'2p', icon:'⭐', title:'2 Sharing Room (Premium)',   desc:'Double occupancy — premium',          key:'rate2p' },
                { sharing:'p',  icon:'🚪', title:'Single / Private Room',      desc:'Single occupancy — private room',     key:'ratep' },
              ].map(({ sharing, icon, title, desc, key }) => (
                <div key={sharing} style={{ border:'2px solid #E8E4DF', borderRadius:12, padding:'20px 24px', marginBottom:16, background:'#F8F6F3', transition:'border-color 0.2s' }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor='#D4AF37'} onMouseLeave={e=>e.currentTarget.style.borderColor='#E8E4DF'}>
                  <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:14 }}>
                    <div style={{ width:44, height:44, borderRadius:10, background:'linear-gradient(135deg,#1B3B5F,#0D1F2D)', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>{icon}</div>
                    <div>
                      <div style={{ fontSize:16, fontWeight:700, color:'#1B3B5F' }}>{title}</div>
                      <div style={{ fontSize:12, color:'#6B6B6B' }}>{desc}</div>
                    </div>
                  </div>
                  <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', marginBottom:6 }}>Rent / Month</div>
                  <div style={{ position:'relative' }}>
                    <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:13, fontWeight:700, color:'#6B6B6B' }}>₹</span>
                    <input type="number" min="0" step="100" value={rateForm[key]}
                      onChange={e => setRateForm(f => ({ ...f, [key]: e.target.value }))}
                      style={{ width:'100%', padding:'10px 10px 10px 26px', border:'2px solid #E8E4DF', borderRadius:8, fontSize:15, fontWeight:600, color:'#1B3B5F', background:'white', outline:'none', fontFamily:'Work Sans,sans-serif' }}
                      onFocus={e=>e.target.style.borderColor='#D4AF37'} onBlur={e=>e.target.style.borderColor='#E8E4DF'} />
                  </div>
                </div>
              ))}
              <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:2, color:'#6B6B6B', marginBottom:20, paddingBottom:8, borderBottom:'2px solid #E8E4DF', marginTop:8 }}>Effective Date &amp; Notes</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:28 }}>
                <div>
                  <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', marginBottom:6 }}>Effective From</div>
                  <input type="date" value={rateForm.effectiveFrom} onChange={e => setRateForm(f => ({ ...f, effectiveFrom: e.target.value }))}
                    style={{ width:'100%', padding:'10px 14px', border:'2px solid #E8E4DF', borderRadius:8, fontSize:14, fontWeight:500, color:'#1B3B5F', background:'white', fontFamily:'Work Sans,sans-serif', outline:'none' }} />
                </div>
                <div>
                  <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', marginBottom:6 }}>Revision Notes (optional)</div>
                  <input type="text" value={rateForm.notes} placeholder="e.g. Annual revision 2026-27"
                    onChange={e => setRateForm(f => ({ ...f, notes: e.target.value }))}
                    style={{ width:'100%', padding:'10px 14px', border:'2px solid #E8E4DF', borderRadius:8, fontSize:14, color:'#1B3B5F', background:'white', fontFamily:'Work Sans,sans-serif', outline:'none' }} />
                </div>
              </div>
              {rates.history && rates.history.length > 0 && (
                <>
                  <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:2, color:'#6B6B6B', marginBottom:16, paddingBottom:8, borderBottom:'2px solid #E8E4DF' }}>Rate Revision History</div>
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                      <thead>
                        <tr>{['Effective From','4 Sharing','3 Sharing','2 Sharing (Twin)','2 Sharing (Premium)','Single/Private','Notes','Status'].map(h=>(
                          <th key={h} style={{ background:'#1B3B5F', padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:600, letterSpacing:1, textTransform:'uppercase', color:'white' }}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {rates.history.map((row, i) => (
                          <tr key={i} style={{ background:i%2===0?'#F8F6F3':'#fff' }}>
                            <td style={{ padding:'10px 14px', borderBottom:'1px solid #E8E4DF', fontWeight:500 }}>{row.effectiveFrom}</td>
                            <td style={{ padding:'10px 14px', borderBottom:'1px solid #E8E4DF' }}>{fmtINR(row.rate4)}</td>
                            <td style={{ padding:'10px 14px', borderBottom:'1px solid #E8E4DF' }}>{fmtINR(row.rate3)}</td>
                            <td style={{ padding:'10px 14px', borderBottom:'1px solid #E8E4DF' }}>{fmtINR(row.rate2)}</td>
                            <td style={{ padding:'10px 14px', borderBottom:'1px solid #E8E4DF' }}>{fmtINR(row.rate2p)}</td>
                            <td style={{ padding:'10px 14px', borderBottom:'1px solid #E8E4DF' }}>{fmtINR(row.ratep)}</td>
                            <td style={{ padding:'10px 14px', borderBottom:'1px solid #E8E4DF', color:'#6B6B6B' }}>{row.notes||'—'}</td>
                            <td style={{ padding:'10px 14px', borderBottom:'1px solid #E8E4DF' }}>
                              {row.isCurrent
                                ? <span style={{ background:'#d1fae5', color:'#065f46', fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20, letterSpacing:0.5, textTransform:'uppercase' }}>Current</span>
                                : <span style={{ color:'#9B9B9B' }}>—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            {/* Footer */}
            <div style={{ padding:'20px 32px 28px', display:'flex', gap:12, justifyContent:'flex-end', borderTop:'1px solid #E8E4DF' }}>
              <button onClick={() => setShowRates(false)} style={{ padding:'12px 28px', border:'2px solid #E8E4DF', background:'white', color:'#6B6B6B', fontWeight:600, fontSize:14, cursor:'pointer', borderRadius:8, fontFamily:'Work Sans,sans-serif' }}>Cancel</button>
              <button onClick={saveRates} disabled={rateSaving} style={{ padding:'12px 32px', border:'none', background:'linear-gradient(135deg,#D4AF37,#c9a227)', color:'white', fontWeight:700, fontSize:14, cursor:rateSaving?'not-allowed':'pointer', borderRadius:8, fontFamily:'Work Sans,sans-serif', opacity:rateSaving?0.7:1 }}>
                {rateSaving ? 'Saving…' : '💾 Save Rate Settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bed action toast */}
      {bedToast && (
        <div style={{ position:'fixed', top:24, right:24, zIndex:99999,
          background:'#2D5F4F', color:'#fff', padding:'14px 22px', borderRadius:8,
          boxShadow:'0 8px 32px rgba(0,0,0,0.22)', fontSize:14, fontWeight:600,
          minWidth:300, animation:'toastIn 0.3s ease' }}>
          {bedToast}
          <style>{'@keyframes toastIn{from{opacity:0;transform:translateY(-16px)}to{opacity:1;transform:translateY(0)}}'}</style>
        </div>
      )}
      {/* Rate saved toast */}
      {rateToast && (
        <div style={{ position:'fixed', bottom:40, left:'50%', transform:'translateX(-50%)', background:'#065f46', color:'white', padding:'14px 28px', borderRadius:10, fontWeight:600, fontSize:14, zIndex:2000, boxShadow:'0 8px 30px rgba(0,0,0,0.2)' }}>
          {rateToast}
        </div>
      )}

    </div>
  );
};

export default RoomAllocationScreen;